// Canonical molecular-biology buffer recipes (amounts per 1 litre of the final
// 1X solution), scaled to any target volume. Masses in grams, liquids in mL.

export type Unit = "g" | "mL";

export interface Ingredient {
  name: string;
  /** amount needed to make 1 L of the buffer */
  per1L: number;
  unit: Unit;
}

export interface Recipe {
  id: string;
  name: string;
  ph?: string;
  ingredients: Ingredient[];
  /** short note, e.g. "Adjust to pH 7.4 with HCl, then top up to volume." */
  note: string;
}

export const RECIPES: Recipe[] = [
  {
    id: "tbs",
    name: "1X TBS (Tris-buffered saline)",
    ph: "7.5",
    ingredients: [
      { name: "Tris base", per1L: 2.42, unit: "g" },
      { name: "Sodium chloride (NaCl)", per1L: 8.0, unit: "g" },
    ],
    note: "Dissolve in ~900 mL water, adjust to pH 7.5 with HCl, then top up to the final volume.",
  },
  {
    id: "tbst",
    name: "1X TBST (TBS + 0.1% Tween-20)",
    ph: "7.5",
    ingredients: [
      { name: "Tris base", per1L: 2.42, unit: "g" },
      { name: "Sodium chloride (NaCl)", per1L: 8.0, unit: "g" },
      { name: "Tween-20", per1L: 1.0, unit: "mL" },
    ],
    note: "Prepare TBS, adjust to pH 7.5, add Tween-20, then top up to the final volume.",
  },
  {
    id: "pbs",
    name: "1X PBS (phosphate-buffered saline)",
    ph: "7.4",
    ingredients: [
      { name: "Sodium chloride (NaCl)", per1L: 8.0, unit: "g" },
      { name: "Potassium chloride (KCl)", per1L: 0.2, unit: "g" },
      { name: "Disodium phosphate (Na₂HPO₄)", per1L: 1.44, unit: "g" },
      { name: "Monopotassium phosphate (KH₂PO₄)", per1L: 0.24, unit: "g" },
    ],
    note: "Dissolve in ~900 mL water, adjust to pH 7.4 with HCl, then top up to the final volume.",
  },
  {
    id: "pbst",
    name: "1X PBST (PBS + 0.1% Tween-20)",
    ph: "7.4",
    ingredients: [
      { name: "Sodium chloride (NaCl)", per1L: 8.0, unit: "g" },
      { name: "Potassium chloride (KCl)", per1L: 0.2, unit: "g" },
      { name: "Disodium phosphate (Na₂HPO₄)", per1L: 1.44, unit: "g" },
      { name: "Monopotassium phosphate (KH₂PO₄)", per1L: 0.24, unit: "g" },
      { name: "Tween-20", per1L: 1.0, unit: "mL" },
    ],
    note: "Prepare PBS, adjust to pH 7.4, add Tween-20, then top up to the final volume.",
  },
  {
    id: "nfdm",
    name: "5% Non-fat dry milk blocking buffer",
    ingredients: [
      { name: "Non-fat dry milk (NFDM)", per1L: 50, unit: "g" },
      { name: "1X TBST", per1L: 1000, unit: "mL" },
    ],
    note: "Dissolve the milk powder in TBST. Make fresh and keep cold; do not store long-term.",
  },
  {
    id: "bsa",
    name: "5% BSA blocking buffer",
    ingredients: [
      { name: "Bovine serum albumin (BSA)", per1L: 50, unit: "g" },
      { name: "1X TBST", per1L: 1000, unit: "mL" },
    ],
    note: "Dissolve BSA gently in TBST to avoid foaming. Preferred over milk for phospho-antibodies.",
  },
];

export interface ScaledIngredient extends Ingredient {
  /** amount for the requested volume */
  amount: number;
}

/** Scale a recipe's ingredients to `volumeMl` millilitres. Null on bad input. */
export function scaleRecipe(
  recipe: Recipe,
  volumeMl: number,
): ScaledIngredient[] | null {
  if (!Number.isFinite(volumeMl) || volumeMl <= 0) return null;
  const factor = volumeMl / 1000;
  return recipe.ingredients.map((ing) => ({
    ...ing,
    amount: ing.per1L * factor,
  }));
}
